// ContactInviteScreen — the /invite/:token public landing.
//
// v0.43.0 — initial. The /invite/<token> URL in the magic-link email
//           drops the recipient here. We POST the token to
//           /api/contact-login, store the resulting session, and
//           redirect to /my-items. Errors fall back to a small "link
//           expired or invalid" card with a link back to the marketing
//           home.
//
// Renders OUTSIDE the AppShell (App.jsx top-level route) so the
// recipient never sees the admin chrome.

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { theme, card, btnPrimary } from '../theme';
import { setContactSession } from '../utils/contact-session';
import DropPinWordmark from '../components/DropPinWordmark';

export default function ContactInviteScreen() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'error'
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/contact-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body?.error || `HTTP ${r.status}`);
        }
        const payload = await r.json();
        if (cancelled) return;
        setContactSession(payload);
        setStatus('ok');
        // Brief pause so the recipient sees "Signed in as ..." before
        // the redirect — feels less abrupt.
        setTimeout(() => navigate('/my-items', { replace: true }), 600);
      } catch (e) {
        if (cancelled) return;
        setErr(e?.message || String(e));
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  return (
    <div style={{
      minHeight: '100vh', background: '#0f172a', color: 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        ...card,
        background: theme.surface, color: theme.text,
        width: 460, maxWidth: '100%',
        padding: 28, textAlign: 'center',
        borderLeft: `5px solid ${theme.brand}`,
      }}>
        {status === 'loading' && (
          <>
            <div style={{ fontSize: 11, color: theme.brand, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}><DropPinWordmark /></div>
            <h1 style={{ fontSize: 20, margin: '0 0 8px' }}>Signing you in…</h1>
            <div style={{ fontSize: 13, color: theme.textLight }}>Validating your invite link.</div>
          </>
        )}
        {status === 'ok' && (
          <>
            <div style={{ fontSize: 11, color: theme.success || '#15803d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Welcome</div>
            <h1 style={{ fontSize: 20, margin: '0 0 8px' }}>You're in.</h1>
            <div style={{ fontSize: 13, color: theme.textLight }}>Loading your items…</div>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ fontSize: 11, color: theme.danger, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Invite invalid</div>
            <h1 style={{ fontSize: 18, margin: '0 0 8px' }}>This invite link can't be used</h1>
            <div style={{ fontSize: 13, color: theme.textLight, marginBottom: 16 }}>
              {err || 'The link may have expired or already been used. Ask the sender to mint a new one.'}
            </div>
            <a href="/" style={{ ...btnPrimary, textDecoration: 'none', display: 'inline-block' }}>Back to <DropPinWordmark color="#fff" /></a>
          </>
        )}
      </div>
    </div>
  );
}
